import { useFeatureEntitlements } from '@frontegg/react';
import { AppstoreOutlined } from '@ant-design/icons';
import { ProIcon } from 'src/icons/Pro.icon';
import { LockIcon } from 'src/icons/Lock.icon';
import { UpgradeTooltip } from './components/Payments/UpgradeTooltip';
import { useIsOP } from 'src/hooks/misc';
import {
  AmbientAi,
  Exchange,
  FileStar,
  GridPlus,
  PuzzlePiece03,
  Brain,
} from '@insight-health-ai/roseate/icons';

const getAutoTemplateMenuItem = (hasAutoTemplateAccess) => {
  if (hasAutoTemplateAccess) {
    return {
      path: '/autotemplate',
      title: <span className="flex items-center">Auto Template</span>,
      icon: <FileStar />,
    };
  }
  return {
    path: '/autotemplate',
    title: (
      <UpgradeTooltip>
        <span className="flex items-center gap-2">
          Auto Template
          <LockIcon />
        </span>
      </UpgradeTooltip>
    ),
    icon: <FileStar />,
  };
};

export const useAmbientSoloSideMenus = () => {
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: hasMemoryAccess } = useFeatureEntitlements('memory');
  const { isEntitled: hasAutoTemplateAccess } =
    useFeatureEntitlements('auto-template');
  const { isEntitled: zoomEncountersAccess } =
    useFeatureEntitlements('zoom-encounters');
  const { isEntitled: templatesExchangeFeature } =
    useFeatureEntitlements('template-exchange');

  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');

  const hasVCAAccess = hasIntakeAccess || hasFollowupAccess;

  const hasMultipleModulesAccess =
    isReferralsModuleEntitled || isPhoneTriageModuleEntitled || hasVCAAccess;
  const isOP = useIsOP();

  if (!hasAmbientAccess) {
    return [];
  }

  const menus = [];
  const dependentMenuItems = [];

  const encountersMenuItem = {
    path: '/encounters',
    title: isOP ? 'Opie AI Scribe' : 'Aura',
    icon: <AmbientAi />,
  };

  const templateExchangeMenuItems = isOP
    ? []
    : templatesExchangeFeature
      ? [
          {
            path: '/templatesexchange',
            title: (
              <span className="flex items-center gap-2">
                Template Exchange
                <ProIcon />
              </span>
            ),
            icon: <Exchange />,
          },
        ]
      : [
          {
            path: '/templatesexchange',
            title: (
              <UpgradeTooltip>
                <span className="flex items-center gap-2">
                  Template Exchange
                  <LockIcon />
                </span>
              </UpgradeTooltip>
            ),
            icon: <AppstoreOutlined />,
          },
        ];

  const templateStudioMenuItems = [
    {
      path: '/setting',
      title: 'Template Studio',
      icon: <GridPlus />,
    },
  ];

  const autoTemplateMenuItems = isOP
    ? []
    : [getAutoTemplateMenuItem(hasAutoTemplateAccess)];

  dependentMenuItems.push(
    ...templateExchangeMenuItems,
    ...templateStudioMenuItems,
    ...autoTemplateMenuItems
  );

  if (hasVCAAccess) {
    if (hasMemoryAccess) {
      dependentMenuItems.push({
        path: '/memory',
        title: 'Memory',
        icon: <Brain />,
      });
    }
  } else {
    dependentMenuItems.push(
      hasMemoryAccess
        ? {
            path: '/memory',
            title: (
              <span className="flex items-center gap-2">
                Memory
                <ProIcon />
              </span>
            ),
            icon: <Brain />,
          }
        : {
            path: '',
            title: (
              <UpgradeTooltip>
                <span className="flex items-center gap-2">
                  Memory
                  <LockIcon />
                </span>
              </UpgradeTooltip>
            ),
            icon: <Brain />,
          }
    );
  }

  menus.push(encountersMenuItem);

  if (dependentMenuItems.length > 0) {
    if (hasMultipleModulesAccess) {
      encountersMenuItem.submenu = dependentMenuItems;
    } else {
      menus.push(...dependentMenuItems);
    }
  }

  if (zoomEncountersAccess) {
    menus.push({
      path: '/integrations',
      title: 'Plugins',
      icon: <PuzzlePiece03 />,
    });
  }

  return menus;
};
